import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  CircularProgress,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper
} from '@mui/material';
import { 
  Add, 
  Delete, 
  DragIndicator,
  ArrowBack,
  Save,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import assessmentService, { AssessmentDetail, Question, Option } from '../services/assessmentService';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const EditSurveyPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<AssessmentDetail | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    instructions: '',
    estimatedMinutes: 30,
    isActive: true,
    redFlagThreshold: 70,
    yellowFlagThreshold: 40,
    greenFlagThreshold: 20
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    loadAssessment();
    loadCategories();
  }, [id]);

  const loadAssessment = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await assessmentService.getAssessmentById(parseInt(id));
      setAssessment(data);
      setFormData({
        title: data.title,
        description: data.description,
        category: data.category,
        instructions: data.instructions,
        estimatedMinutes: data.estimatedMinutes,
        isActive: data.isActive,
        redFlagThreshold: data.redFlagThreshold,
        yellowFlagThreshold: data.yellowFlagThreshold,
        greenFlagThreshold: data.greenFlagThreshold
      });
      setQuestions(data.questions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await assessmentService.getAssessmentCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleBack = () => {
    navigate('/manage-surveys');
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuestionChange = (questionIndex: number, field: string, value: any) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      [field]: value
    };
    setQuestions(updatedQuestions);
  };

  const handleOptionChange = (questionIndex: number, optionIndex: number, field: string, value: any) => {
    const updatedQuestions = [...questions];
    const updatedOptions = [...(updatedQuestions[questionIndex].options || [])];
    updatedOptions[optionIndex] = {
      ...updatedOptions[optionIndex],
      [field]: value
    };
    updatedQuestions[questionIndex].options = updatedOptions;
    setQuestions(updatedQuestions);
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: Date.now(), // Temporary ID
      questionText: '',
      questionType: 'multiple_choice',
      isRequired: true,
      orderIndex: questions.length,
      pointsIfApplicable: 0,
      options: []
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    // Update order indexes
    updatedQuestions.forEach((q, i) => {
      q.orderIndex = i;
    });
    setQuestions(updatedQuestions);
  };

  const addOption = (questionIndex: number) => {
    const updatedQuestions = [...questions];
    const newOption: Option = {
      id: Date.now(), // Temporary ID
      optionText: '',
      pointValue: 0,
      orderIndex: updatedQuestions[questionIndex].options?.length || 0,
      flagIfSelected: false
    };
    updatedQuestions[questionIndex].options = [
      ...(updatedQuestions[questionIndex].options || []),
      newOption
    ];
    setQuestions(updatedQuestions);
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options?.filter(
      (_, i) => i !== optionIndex
    );
    // Update order indexes
    updatedQuestions[questionIndex].options?.forEach((opt, i) => {
      opt.orderIndex = i;
    });
    setQuestions(updatedQuestions);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order indexes
    items.forEach((item, index) => {
      item.orderIndex = index;
    });

    setQuestions(items);
  };

  const handleSave = async () => {
    if (!assessment) return;

    try {
      setSaving(true);
      setError(null);

      // Validate form
      if (!formData.title.trim()) {
        setError('Title is required');
        return;
      }

      if (questions.length === 0) {
        setError('At least one question is required');
        return;
      }

      // Validate questions
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        if (!q.questionText.trim()) {
          setError(`Question ${i + 1} text is required`);
          return;
        }
        if (q.questionType === 'multiple_choice' && (!q.options || q.options.length < 2)) {
          setError(`Question ${i + 1} must have at least 2 options`);
          return;
        }
      }

      const updatedAssessment = {
        ...formData,
        questions: questions
      };

      await assessmentService.updateAssessment(assessment.id, updatedAssessment);
      toast.success('Survey updated successfully');
      navigate('/manage-surveys');
    } catch (err: any) {
      setError(err.message || 'Failed to update survey');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Edit Survey
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Edit Survey
            </Typography>
            <Button onClick={handleBack} startIcon={<ArrowBack />}>
              Back to Manage Surveys
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleBack}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    {categories.map(category => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Minutes"
                  type="number"
                  value={formData.estimatedMinutes}
                  onChange={(e) => handleInputChange('estimatedMinutes', parseInt(e.target.value) || 0)}
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instructions"
                  value={formData.instructions}
                  onChange={(e) => handleInputChange('instructions', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Risk Thresholds */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Risk Thresholds
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Red Flag Threshold"
                  type="number"
                  value={formData.redFlagThreshold}
                  onChange={(e) => handleInputChange('redFlagThreshold', parseInt(e.target.value) || 0)}
                  InputProps={{
                    inputProps: { min: 0, max: 100 },
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Yellow Flag Threshold"
                  type="number"
                  value={formData.yellowFlagThreshold}
                  onChange={(e) => handleInputChange('yellowFlagThreshold', parseInt(e.target.value) || 0)}
                  InputProps={{
                    inputProps: { min: 0, max: 100 },
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Green Flag Threshold"
                  type="number"
                  value={formData.greenFlagThreshold}
                  onChange={(e) => handleInputChange('greenFlagThreshold', parseInt(e.target.value) || 0)}
                  InputProps={{
                    inputProps: { min: 0, max: 100 },
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Questions ({questions.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addQuestion}
              >
                Add Question
              </Button>
            </Box>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {questions.map((question, qIndex) => (
                      <Draggable
                        key={question.id}
                        draggableId={question.id.toString()}
                        index={qIndex}
                      >
                        {(provided) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{ mb: 2, p: 2 }}
                            variant="outlined"
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <div {...provided.dragHandleProps}>
                                <DragIndicator sx={{ mt: 1, cursor: 'grab' }} />
                              </div>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                  <Typography variant="subtitle1">
                                    Question {qIndex + 1}
                                  </Typography>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={question.isRequired}
                                        onChange={(e) => handleQuestionChange(qIndex, 'isRequired', e.target.checked)}
                                        size="small"
                                      />
                                    }
                                    label="Required"
                                  />
                                  <Box sx={{ flex: 1 }} />
                                  <IconButton
                                    size="small"
                                    onClick={() => removeQuestion(qIndex)}
                                    color="error"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>

                                <TextField
                                  fullWidth
                                  label="Question Text"
                                  value={question.questionText}
                                  onChange={(e) => handleQuestionChange(qIndex, 'questionText', e.target.value)}
                                  sx={{ mb: 2 }}
                                  required
                                />

                                <Box sx={{ mb: 2 }}>
                                  <FormControl fullWidth>
                                    <InputLabel>Question Type</InputLabel>
                                    <Select
                                      value={question.questionType}
                                      label="Question Type"
                                      onChange={(e) => handleQuestionChange(qIndex, 'questionType', e.target.value)}
                                    >
                                      <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                                      <MenuItem value="text">Text Response</MenuItem>
                                      <MenuItem value="scale">Scale</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>

                                {question.questionType === 'multiple_choice' && (
                                  <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography variant="subtitle2">Options</Typography>
                                      <Button
                                        size="small"
                                        startIcon={<Add />}
                                        onClick={() => addOption(qIndex)}
                                      >
                                        Add Option
                                      </Button>
                                    </Box>
                                    {question.options?.map((option, optIndex) => (
                                      <Box key={option.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <TextField
                                          fullWidth
                                          label={`Option ${optIndex + 1}`}
                                          value={option.optionText}
                                          onChange={(e) => handleOptionChange(qIndex, optIndex, 'optionText', e.target.value)}
                                          size="small"
                                        />
                                        <TextField
                                          label="Points"
                                          type="number"
                                          value={option.pointValue}
                                          onChange={(e) => handleOptionChange(qIndex, optIndex, 'pointValue', parseInt(e.target.value) || 0)}
                                          size="small"
                                          sx={{ width: 100 }}
                                        />
                                        <FormControlLabel
                                          control={
                                            <Switch
                                              checked={option.flagIfSelected}
                                              onChange={(e) => handleOptionChange(qIndex, optIndex, 'flagIfSelected', e.target.checked)}
                                              size="small"
                                            />
                                          }
                                          label="Flag"
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() => removeOption(qIndex, optIndex)}
                                          color="error"
                                        >
                                          <Delete />
                                        </IconButton>
                                      </Box>
                                    ))}
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default EditSurveyPage;